import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost } from './services_1/apiClient';
import { calculateAdjustment } from './utils_1/forecastUtils';

// Routing & Auth
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginWindow from './components/views/LoginWindow/LoginWindow';
import SignupWindow from './components/views/SignupWindow/SignupWindow';
import ForgotPassword from './components/views/ForgotPassword/ForgotPassword';
import ResetPassword from './components/views/ResetPassword/ResetPassword';

// Components
import Layout from './components/Layout/Layout';
import DashboardWindow from './components/views/DashboardWindow/DashboardWindow';
import CustomerOrdersWindow from './components/views/CustomerOrdersWindow/CustomerOrdersVizPanel';
import AggregationWindow from './components/views/AggregationWindow/AggregationWindow';
import AdHocWindow from './components/views/AdHocWindow/AdHocWindow';
import SimulationWindow from './components/views/SimulationWindow/SimulationWindow';
import PromotionalWindow from './components/views/PromotionalWindow/PromotionalWindow';
import ForecAICopilot from './components/Chatbot/ForecAICopilot';

// Styles
import './App.css';
import './components/Charts/Charts.css';
import './components/ControlPanel/DailyForecastControl.css';
import './components/ControlPanel/PromotionalAnalysisControl.css';
import './components/ScenarioPanel/ScenarioPanel.css';
import './components/Layout/Layout.css';
import './components/views/DashboardWindow/DashboardWindow.css';
import './components/views/SimulationWindow/SimulationWindow.css';
import './components/views/PromotionalWindow/PromotionalWindow.css';
import './components/VersionSelector.css';
import { deriveGranularityFromLocation } from './utils_1/locationRouting';


// Enhanced Top Header Component with Export Dropdown
const TopHeader = ({
  onExport,
  onTitleClick,
  isAdmin = false,
  adhocRequests = [],
  adhocRequestsLoading = false,
  onRefreshAdhocRequests,
  onApproveAdhocRequest,
  onRejectAdhocRequest,
  processingAdhocRequestId = '',
  pendingUsers = [],
  pendingUsersLoading = false,
  onRefreshPendingUsers,
  onApproveUser,
  onRejectUser,
  processingPendingUserId = '',
}) => {
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [pendingUsersOpen, setPendingUsersOpen] = useState(false);
  const pendingRequests = Array.isArray(adhocRequests) ? adhocRequests : [];
  const pendingCount = pendingRequests.length;
  const pendingUserCount = Array.isArray(pendingUsers) ? pendingUsers.length : 0;

  const handleExportFormat = (format) => {
    onExport(format);
    setExportDropdownOpen(false);
  };

  return (
    <header className="app-header">
      <div className="header-container">
        <div className="header-content">
          <div className="header-branding">
            <div className="title-group">
              <h1
                className="app-title app-title-clickable"
                onClick={onTitleClick}
                title="Go to Dashboard"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onTitleClick?.()}
              >ForeC.AI</h1>
              <span className="powered-by-text">powered by</span>
            </div>
            <img src="/Vdart_logo.png" alt="VDart Digital" className="brand-logo" />
          </div>
          <div className="header-controls">
            {isAdmin && (
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setNotificationsOpen(v => !v)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.65rem 1rem',
                    borderRadius: '999px',
                    fontSize: '0.85rem',
                  }}
                >
                  <span>🔔 Notifications</span>
                  {pendingCount > 0 && (
                    <span style={{
                      minWidth: '1.35rem',
                      height: '1.35rem',
                      borderRadius: '999px',
                      padding: '0 0.35rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#ef4444',
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}>
                      {pendingCount}
                    </span>
                  )}
                </button>
                {notificationsOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    right: 0,
                    width: '420px',
                    maxWidth: 'calc(100vw - 2rem)',
                    maxHeight: '520px',
                    overflowY: 'auto',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.18)',
                    zIndex: 1005,
                    padding: '0.85rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.8rem' }}>
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>Ad-Hoc Approval Queue</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {pendingCount > 0 ? `${pendingCount} pending request${pendingCount === 1 ? '' : 's'}` : 'No pending requests'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => onRefreshAdhocRequests?.()}
                        disabled={adhocRequestsLoading}
                        style={{ padding: '0.5rem 0.8rem', fontSize: '0.8rem' }}
                      >
                        {adhocRequestsLoading ? 'Refreshing…' : 'Refresh'}
                      </button>
                    </div>

                    {adhocRequestsLoading ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>Loading requests…</div>
                    ) : pendingRequests.length === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
                        No pending ad-hoc requests right now.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {pendingRequests.map((request) => {
                          const isBusy = processingAdhocRequestId === request.request_id;
                          return (
                            <div key={request.request_id} style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: '14px',
                              padding: '0.85rem',
                              background: '#f8fafc',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                <div>
                                  <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                    {request.sku_id || 'SKU'} • {request.channel || 'All Channels'} • {request.location || 'All Locations'}
                                  </div>
                                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                    {request.target_date || '—'} • {request.requested_by_role || 'user'}{request.requested_by_user ? ` • ${request.requested_by_user}` : ''}
                                  </div>
                                </div>
                                <span style={{
                                  alignSelf: 'flex-start',
                                  fontSize: '0.72rem',
                                  padding: '0.25rem 0.55rem',
                                  borderRadius: '999px',
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 700,
                                }}>
                                  Pending
                                </span>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.45rem 0.75rem', fontSize: '0.8rem', color: '#334155' }}>
                                <div><strong>Current:</strong> {(() => {
                                  const val = request.current_forecast;
                                  const num = Number(val);
                                  return !isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (val ?? '—');
                                })()}</div>
                                <div><strong>New:</strong> {(() => {
                                  const val = request.new_forecast_value;
                                  const num = Number(val);
                                  return !isNaN(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (val ?? '—');
                                })()}</div>
                                <div><strong>Type:</strong> {request.adjustment_type ?? '—'}</div>
                                <div><strong>Reason:</strong> {request.reason_category ?? '—'}</div>
                              </div>

                              {request.notes && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#475569' }}>
                                  <strong>Notes:</strong> {request.notes}
                                </div>
                              )}

                              <div style={{ display: 'flex', gap: '0.55rem', marginTop: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={isBusy}
                                  onClick={() => onApproveAdhocRequest?.(request.request_id)}
                                  style={{ padding: '0.5rem 0.85rem', fontSize: '0.8rem' }}
                                >
                                  {isBusy ? 'Processing…' : 'Approve'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  disabled={isBusy}
                                  onClick={() => onRejectAdhocRequest?.(request.request_id)}
                                  style={{ padding: '0.5rem 0.85rem', fontSize: '0.8rem' }}
                                >
                                  Reject
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {isAdmin && (
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setPendingUsersOpen(v => !v); setNotificationsOpen(false); }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.65rem 1rem',
                    borderRadius: '999px',
                    fontSize: '0.85rem',
                  }}
                >
                  <span>👥 Pending Users</span>
                  {pendingUserCount > 0 && (
                    <span style={{
                      minWidth: '1.35rem',
                      height: '1.35rem',
                      borderRadius: '999px',
                      padding: '0 0.35rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#f59e0b',
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                    }}>
                      {pendingUserCount}
                    </span>
                  )}
                </button>
                {pendingUsersOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 10px)',
                    right: 0,
                    width: '480px',
                    maxWidth: 'calc(100vw - 2rem)',
                    maxHeight: '520px',
                    overflowY: 'auto',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.18)',
                    zIndex: 1005,
                    padding: '0.85rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.8rem' }}>
                      <div>
                        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>Pending User Approvals</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {pendingUserCount > 0 ? `${pendingUserCount} pending request${pendingUserCount === 1 ? '' : 's'}` : 'No pending requests'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => onRefreshPendingUsers?.()}
                        disabled={pendingUsersLoading}
                        style={{ padding: '0.5rem 0.8rem', fontSize: '0.8rem' }}
                      >
                        {pendingUsersLoading ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </div>

                    {pendingUsersLoading ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b' }}>Loading users...</div>
                    ) : pendingUserCount === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
                        No pending user registrations.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {pendingUsers.map((user) => {
                          const isBusy = processingPendingUserId === user.id;
                          const regDate = user.created_at ? new Date(user.created_at).toLocaleDateString() : '—';
                          return (
                            <div key={user.id} style={{
                              border: '1px solid #e2e8f0',
                              borderRadius: '14px',
                              padding: '0.85rem',
                              background: '#f8fafc',
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                <div>
                                  <div style={{ fontWeight: 700, color: '#0f172a' }}>
                                    {user.full_name}
                                  </div>
                                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                                    @{user.username} &bull; {user.email}
                                  </div>
                                </div>
                                <span style={{
                                  alignSelf: 'flex-start',
                                  fontSize: '0.72rem',
                                  padding: '0.25rem 0.55rem',
                                  borderRadius: '999px',
                                  background: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap',
                                }}>
                                  Pending
                                </span>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.45rem 0.75rem', fontSize: '0.8rem', color: '#334155' }}>
                                <div><strong>Department:</strong> {user.department || '—'}</div>
                                <div><strong>Role:</strong> {(user.role_name || '').replace(/_/g, ' ')}</div>
                                <div><strong>Registered:</strong> {regDate}</div>
                              </div>

                              <div style={{ display: 'flex', gap: '0.55rem', marginTop: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={isBusy}
                                  onClick={() => onApproveUser?.(user.id)}
                                  style={{ padding: '0.5rem 0.85rem', fontSize: '0.8rem' }}
                                >
                                  {isBusy ? 'Processing...' : 'Approve'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  disabled={isBusy}
                                  onClick={() => onRejectUser?.(user.id)}
                                  style={{ padding: '0.5rem 0.85rem', fontSize: '0.8rem' }}
                                >
                                  Reject
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="export-dropdown">
              <button
                onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                className="btn btn-success dropdown-toggle"
              >
                📊 Export Data ▼
              </button>
              {exportDropdownOpen && (
                <div className="dropdown-menu">
                  <button
                    onClick={() => handleExportFormat('csv')}
                    className="dropdown-item"
                  >
                    📄 Export as CSV
                  </button>
                  <button
                    onClick={() => handleExportFormat('json')}
                    className="dropdown-item"
                  >
                    📋 Export as JSON
                  </button>
                  <button
                    onClick={() => handleExportFormat('xml')}
                    className="dropdown-item"
                  >
                    🔖 Export as XML
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

const COPILOT_WIDTH_STORAGE_KEY = 'forec_ai_copilot_width';

const clampCopilotWidth = (width) => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const minWidth = viewportWidth <= 1100 ? Math.min(430, viewportWidth - 20) : 390;
  const maxWidth = viewportWidth <= 1100 ? Math.min(430, viewportWidth - 20) : Math.min(680, Math.max(420, viewportWidth * 0.55));
  const numeric = Number(width);
  if (!Number.isFinite(numeric)) {
    return Math.round(Math.min(Math.max(viewportWidth * 0.35, minWidth), maxWidth));
  }
  return Math.round(Math.min(Math.max(numeric, minWidth), maxWidth));
};

const loadInitialCopilotWidth = () => {
  if (typeof window === 'undefined') return 448;
  const stored = Number(window.localStorage.getItem(COPILOT_WIDTH_STORAGE_KEY));
  return clampCopilotWidth(Number.isFinite(stored) && stored > 0 ? stored : window.innerWidth * 0.35);
};

// Unified Export Functions Hook - all windows in one file
const ROLE_SECTIONS = {
  demand_planner: ['dashboard', 'simulation', 'promotional', 'adhoc', 'aggregation', 'customer_orders'],
  brand_manager: ['dashboard', 'promotional', 'aggregation', 'customer_orders'],
  sales_manager: ['dashboard', 'promotional', 'aggregation', 'customer_orders'],
  sc_analyst: ['dashboard', 'simulation', 'aggregation', 'adhoc', 'customer_orders', 'promotional'],
  executive: ['dashboard', 'promotional', 'aggregation', 'customer_orders'],
  admin: ['dashboard', 'simulation', 'promotional', 'adhoc', 'aggregation', 'customer_orders']
};

const useExportFunctions = (
  dataSummary,
  dailyForecastData,
  simpleForecastData,
  promotionalData,
  aggregationState,
  adHocData,
  customerOrdersState,
  userRole
) => {

  const buildExportData = useCallback(() => {
    const timestamp = new Date().toISOString().split('T')[0];
    const allowedSections = ROLE_SECTIONS[userRole] || ROLE_SECTIONS['demand_planner'];

    const exportData = {
      exportMetadata: { timestamp, exportedAt: new Date().toISOString(), version: '1.0', role: userRole || 'demand_planner' },
    };

    if (allowedSections.includes('dashboard')) {
      exportData.dashboard = {
        dataSummary: dataSummary ? {
          basicStats: {
            totalRecords: dataSummary.basic_stats?.total_records || 0,
            dateRange: { start: dataSummary.basic_stats?.date_range?.start || null, end: dataSummary.basic_stats?.date_range?.end || null }
          },
          granularityStats: {
            uniqueSkus: dataSummary.fr1_granularity_stats?.unique_skus || 0,
            uniqueChannels: dataSummary.fr1_granularity_stats?.unique_channels || 0,
            uniqueLocations: dataSummary.fr1_granularity_stats?.unique_locations || 0,
            uniqueCombinations: dataSummary.fr1_granularity_stats?.unique_sku_channel_location_combinations || 0
          },
          promotionalStats: {
            totalPromotionalRecords: dataSummary.fr2_promotional_stats?.total_promotional_records || 0,
            promotionalPercentage: dataSummary.fr2_promotional_stats?.promotional_percentage || 0
          }
        } : null,
        dailyForecast: dailyForecastData ? {
          granularityLevel: dailyForecastData.granularity_level,
          forecasts: (dailyForecastData.forecasts || []).map(f => ({
            skuId: f.sku_id || 'aggregated',
            dates: f.dates || [],
            baseForecast: f.base_forecast || [],
            promotionalForecast: f.promotional_forecast || [],
            totalForecast: f.total_forecast || [],
            confidenceUpper: f.confidence_upper || [],
            confidenceLower: f.confidence_lower || [],
            historicalBaseAvg: f.historical_base_avg || 0,
            historicalPromoAvg: f.historical_promo_avg || 0,
            trendSlope: f.trend_slope || 0,
            hasHistoricalData: f.has_historical_data || false,
            forecastingMethod: f.forecasting_method || '',
            dataSource: f.data_source || '',
            anomalyFlags: f.anomaly_flags || [],
            anomalyNotes: f.anomaly_notes || [],
            anomalyReasons: f.anomaly_reasons || []
          }))
        } : null
      };
    }

    if (allowedSections.includes('simulation')) {
      exportData.simulation = {
        scenarioForecast: simpleForecastData ? {
          labels: simpleForecastData.labels || [],
          values: simpleForecastData.values || [],
          confidenceUpper: simpleForecastData.confidence_upper || [],
          confidenceLower: simpleForecastData.confidence_lower || [],
          featureCount: simpleForecastData.feature_count || 0,
          featuresUsed: simpleForecastData.features_used || []
        } : null
      };
    }

    if (allowedSections.includes('promotional')) {
      exportData.promotional = {
        promotionalAnalysis: promotionalData ? {
          dates: promotionalData.dates || [],
          baseDemandForecast: promotionalData.base_demand_forecast || [],
          promotionalDemandForecast: promotionalData.promotional_demand_forecast || [],
          confidenceUpper: promotionalData.confidence_upper || [],
          confidenceLower: promotionalData.confidence_lower || [],
          historicalSummary: promotionalData.historical_summary || {},
          promotionalImpactAnalysis: promotionalData.promotional_impact_analysis || {},
          dataQuality: promotionalData.data_quality || {}
        } : null
      };
    }

    if (allowedSections.includes('adhoc')) {
      exportData.adHoc = {
        adjustmentResult: adHocData ? {
          skuId: adHocData.sku_id || '',
          channel: adHocData.channel || '',
          location: adHocData.location || '',
          targetDate: adHocData.target_date || '',
          currentForecast: adHocData.current_forecast || 0,
          newForecastValue: adHocData.new_forecast_value || 0,
          adjustmentType: adHocData.adjustment_type || '',
          adjustmentValue: adHocData.adjustment_value || 0,
          reasonCategory: adHocData.reason_category || '',
          notes: adHocData.notes || '',
          timestamp: adHocData.timestamp || null,
          adjustmentSummary: adHocData.adjustment_summary || null,
          dataContext: adHocData.data_context || null
        } : null
      };
    }

    if (allowedSections.includes('aggregation')) {
      exportData.aggregation = {
        aggregationData: aggregationState?.aggregationData || null,
        drillData: aggregationState?.drillData || null,
        rollData: aggregationState?.rollData || null
      };
    }

    if (allowedSections.includes('customer_orders')) {
      exportData.customerOrders = customerOrdersState ? {
        filters: customerOrdersState.filters || null,
        stats: {
          totalUnits: customerOrdersState.totalUnits || 0,
          avgPerDay: customerOrdersState.avgPerDay || 0,
          horizonDays: customerOrdersState.horizonDays || 0,
          promoDays: customerOrdersState.promoDays || null,
          rowsCount: customerOrdersState.rowsCount || 0
        },
        alerts: customerOrdersState.alerts || [],
        alertCount: customerOrdersState.alertCount || 0,
        areaTrend: customerOrdersState.areaTrend || [],
        customerMix: customerOrdersState.customerMix || [],
        skuMix: customerOrdersState.skuMix || [],
        locationMix: customerOrdersState.locationMix || [],
        sampleRows: customerOrdersState.sampleRows || []
      } : null;
    }

    return exportData;
  }, [dataSummary, dailyForecastData, simpleForecastData, promotionalData, aggregationState, adHocData, customerOrdersState, userRole]);

  const escapeXML = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const objectToXML = (obj, indent = 0) => {
    const sp = '  '.repeat(indent);
    let xml = '';
    for (const [key, value] of Object.entries(obj)) {
      const tag = key.replace(/[^a-zA-Z0-9_]/g, '_');
      if (value === null || value === undefined) {
        xml += `${sp}<${tag} />\n`;
      } else if (Array.isArray(value)) {
        xml += `${sp}<${tag}>\n`;
        value.forEach((item, idx) => {
          if (typeof item === 'object' && item !== null) {
            xml += `${sp}  <item index="${idx}">\n${objectToXML(item, indent + 2)}${sp}  </item>\n`;
          } else {
            xml += `${sp}  <item index="${idx}">${escapeXML(item ?? '')}</item>\n`;
          }
        });
        xml += `${sp}</${tag}>\n`;
      } else if (typeof value === 'object') {
        xml += `${sp}<${tag}>\n${objectToXML(value, indent + 1)}${sp}</${tag}>\n`;
      } else {
        xml += `${sp}<${tag}>${escapeXML(value)}</${tag}>\n`;
      }
    }
    return xml;
  };

  const triggerDownload = useCallback((content, mimeType, filename) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }, []);

  const handleExport = useCallback((format) => {
    const data = buildExportData();
    const ts = new Date().toISOString().split('T')[0];
    const base = `forecai_export_${ts}`;

    if (format === 'json') {
      triggerDownload(JSON.stringify(data, null, 2), 'application/json', `${base}.json`);
    } else if (format === 'xml') {
      const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<forecai_export>\n${objectToXML(data, 1)}</forecai_export>`;
      triggerDownload(xml, 'application/xml', `${base}.xml`);
    } else if (format === 'csv') {
      const rows = [['Section', 'Sub Key', 'Index', 'Field', 'Value']];

      const pushKV = (section, sub, idx, obj) => {
        for (const [k, v] of Object.entries(obj || {})) {
          if (v && typeof v === 'object' && !Array.isArray(v)) {
            pushKV(section, `${sub ? sub + '.' : ''}${k}`, idx, v);
          } else if (Array.isArray(v)) {
            v.forEach((item, i) => {
              if (item && typeof item === 'object') {
                pushKV(section, `${sub ? sub + '.' : ''}${k}`, `${idx || 0}_${i}`, item);
              } else {
                rows.push([section, sub || '', idx || i, k, item ?? '']);
              }
            });
          } else {
            rows.push([section, sub || '', idx || '', k, v ?? '']);
          }
        }
      };

      if (data.dashboard?.dataSummary) {
        const ds = data.dashboard.dataSummary;
        pushKV('Dashboard_Summary', 'basicStats', '', ds.basicStats || {});
        pushKV('Dashboard_Summary', 'granularityStats', '', ds.granularityStats || {});
        pushKV('Dashboard_Summary', 'promotionalStats', '', ds.promotionalStats || {});
      }
      if (data.dashboard?.dailyForecast?.forecasts) {
        data.dashboard.dailyForecast.forecasts.forEach((f, i) => {
          rows.push(['DailyForecast', 'forecast', i, 'skuId', f.skuId]);
          (f.dates || []).forEach((d, j) => {
            rows.push(['DailyForecast', f.skuId, d, 'baseForecast', f.baseForecast?.[j] ?? '']);
            rows.push(['DailyForecast', f.skuId, d, 'totalForecast', f.totalForecast?.[j] ?? '']);
            rows.push(['DailyForecast', f.skuId, d, 'confidenceUpper', f.confidenceUpper?.[j] ?? '']);
            rows.push(['DailyForecast', f.skuId, d, 'confidenceLower', f.confidenceLower?.[j] ?? '']);
          });
        });
      }
      if (data.simulation?.scenarioForecast) {
        const sf = data.simulation.scenarioForecast;
        (sf.labels || []).forEach((l, i) => {
          rows.push(['Simulation', '', l, 'value', sf.values?.[i] ?? '']);
          rows.push(['Simulation', '', l, 'confidenceUpper', sf.confidenceUpper?.[i] ?? '']);
          rows.push(['Simulation', '', l, 'confidenceLower', sf.confidenceLower?.[i] ?? '']);
        });
      }
      if (data.promotional?.promotionalAnalysis) {
        const pa = data.promotional.promotionalAnalysis;
        (pa.dates || []).forEach((d, i) => {
          rows.push(['Promotional', '', d, 'baseDemand', pa.baseDemandForecast?.[i] ?? '']);
          rows.push(['Promotional', '', d, 'promoDemand', pa.promotionalDemandForecast?.[i] ?? '']);
        });
      }
      if (data.adHoc?.adjustmentResult) {
        pushKV('AdHoc', 'adjustment', '', data.adHoc.adjustmentResult);
      }
      if (data.aggregation?.aggregationData) {
        pushKV('Aggregation', 'data', '', data.aggregation.aggregationData);
      }
      if (data.customerOrders) {
        const co = data.customerOrders;
        rows.push(['CustomerOrders', 'stats', '', 'totalUnits', co.stats?.totalUnits ?? '']);
        rows.push(['CustomerOrders', 'stats', '', 'avgPerDay', co.stats?.avgPerDay ?? '']);
        rows.push(['CustomerOrders', 'stats', '', 'horizonDays', co.stats?.horizonDays ?? '']);
        rows.push(['CustomerOrders', 'stats', '', 'rowsCount', co.stats?.rowsCount ?? '']);
        rows.push(['CustomerOrders', 'alerts', '', 'count', co.alertCount ?? 0]);
        (co.alerts || []).forEach((a, i) => rows.push(['CustomerOrders', 'alert', i, 'message', a.message || a]));
        (co.customerMix || []).forEach((c, i) => rows.push(['CustomerOrders', 'customerMix', i, 'name', `${c.name || c.customer || ''}|${c.value || c.count || 0}`]));
        (co.skuMix || []).forEach((s, i) => rows.push(['CustomerOrders', 'skuMix', i, 'name', `${s.name || s.sku_id || ''}|${s.value || s.count || 0}`]));
        (co.locationMix || []).forEach((l, i) => rows.push(['CustomerOrders', 'locationMix', i, 'name', `${l.name || l.location || ''}|${l.value || l.count || 0}`]));
      }

      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      triggerDownload(csv, 'text/csv', `${base}.csv`);
    }
  }, [buildExportData, triggerDownload]);

  return handleExport;
};

// Error Display Component
const ErrorDisplay = ({ error, onClose }) => (
  error && (
    <div className="alert alert-error">
      <span className="alert-icon">⚠️</span>
      <p className="alert-message">{error}</p>
      <button className="alert-close" onClick={onClose}>×</button>
    </div>
  )
);

const GlobalLoadingOverlay = ({ visible, message }) => {
  if (!visible) return null;
  return (
    <div className="global-loading-overlay" role="status" aria-live="polite" aria-busy="true">
      <div className="global-loading-card">
        <div className="spinner global-loading-spinner" />
        <strong>{message}</strong>
        <span>Please wait while the forecast and charts are being prepared.</span>
      </div>
    </div>
  );
};

// CSS for dropdown styling
const dropdownStyles = `
.export-dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background: #28a745;
  color: white;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.dropdown-toggle:hover {
  background: #218838;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 180px;
  margin-top: 4px;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background: #f8f9fa;
}

.dropdown-item:first-child {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.dropdown-item:last-child {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
`;

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ width: '40px', height: '40px' }}></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Main App Component
const DashboardApp = () => {
  const { user } = useAuth();
  const isExecutive = user?.role === 'executive';
  const isAdmin = user?.role === 'admin';
  // State
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  const [dataSummary, setDataSummary] = useState(null);
  const [dailyForecastData, setDailyForecastData] = useState(null);
  const [dashboardKpiContext, setDashboardKpiContext] = useState(null);
  const [aggregationState, setAggregationState] = useState({
    loading: false,
    error: null,
    selectedSkuId: '',
    aggregationData: null,
    drillData: null,
  });
  const [customerOrdersState, setCustomerOrdersState] = useState({
    loading: false,
    error: '',
    filters: null,
    stats: null,
    totalUnits: 0,
    avgPerDay: 0,
    horizonDays: 0,
    promoDays: null,
    rowsCount: 0,
    areaTrend: null,
    alerts: [],
    alertCount: 0,
    customerMix: [],
    skuMix: [],
    locationMix: [],
    sampleRows: [],
  });
  const [adHocData, setAdHocData] = useState(null);
  const [adHocLoading, setAdHocLoading] = useState(false);
  const [promotionalData, setPromotionalData] = useState(null);
  const [simpleForecastData, setSimpleForecastData] = useState(null);
  const [dashboardChartContext, setDashboardChartContext] = useState(null);
  const [simulationChartContext, setSimulationChartContext] = useState(null);
  const [simulationScenarioContext, setSimulationScenarioContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeWindow, setActiveWindow] = useState('dashboard');
  const [visitedWindows, setVisitedWindows] = useState(['dashboard']);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotWidth, setCopilotWidth] = useState(loadInitialCopilotWidth);
  const [adhocRequests, setAdhocRequests] = useState([]);
  const [adhocRequestsLoading, setAdhocRequestsLoading] = useState(false);
  const [processingAdhocRequestId, setProcessingAdhocRequestId] = useState('');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingUsersLoading, setPendingUsersLoading] = useState(false);
  const [processingPendingUserId, setProcessingPendingUserId] = useState('');
  const scenarioRequestSeqRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COPILOT_WIDTH_STORAGE_KEY, String(copilotWidth));
  }, [copilotWidth]);

  useEffect(() => {
    setVisitedWindows(prev => (prev.includes(activeWindow) ? prev : [...prev, activeWindow]));
  }, [activeWindow]);

  useEffect(() => {
    if (isExecutive && activeWindow === 'simulation') {
      setActiveWindow('dashboard');
    }
  }, [activeWindow, isExecutive]);

  const loadAdhocRequests = useCallback(async () => {
    if (!isAdmin) {
      setAdhocRequests([]);
      return;
    }
    setAdhocRequestsLoading(true);
    try {
      const data = await apiGet('/api/adhoc-requests?status=pending');
      setAdhocRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (err) {
      console.error('Failed to load ad-hoc requests:', err);
      setAdhocRequests([]);
    } finally {
      setAdhocRequestsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setAdhocRequests([]);
      return undefined;
    }
    loadAdhocRequests();
    const timer = window.setInterval(() => {
      loadAdhocRequests();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [isAdmin, loadAdhocRequests]);

  const loadPendingUsers = useCallback(async () => {
    if (!isAdmin) {
      setPendingUsers([]);
      return;
    }
    setPendingUsersLoading(true);
    try {
      const data = await apiGet('/api/auth/pending-users');
      setPendingUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (err) {
      console.error('Failed to load pending users:', err);
      setPendingUsers([]);
    } finally {
      setPendingUsersLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setPendingUsers([]);
      return undefined;
    }
    loadPendingUsers();
    const timer = window.setInterval(() => {
      loadPendingUsers();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [isAdmin, loadPendingUsers]);

  const handleApproveUser = useCallback(async (userId) => {
    if (!userId) return;
    setProcessingPendingUserId(userId);
    try {
      await apiPost(`/api/auth/approve/${userId}`);
      await loadPendingUsers();
    } catch (err) {
      console.error('Failed to approve user:', err);
      setError(`Failed to approve user: ${err.message}`);
    } finally {
      setProcessingPendingUserId('');
    }
  }, [loadPendingUsers]);

  const handleRejectUser = useCallback(async (userId) => {
    if (!userId) return;
    setProcessingPendingUserId(userId);
    try {
      await apiPost(`/api/auth/reject/${userId}`);
      await loadPendingUsers();
    } catch (err) {
      console.error('Failed to reject user:', err);
      setError(`Failed to reject user: ${err.message}`);
    } finally {
      setProcessingPendingUserId('');
    }
  }, [loadPendingUsers]);

  // Simplified model params - now only used for initial data loading
  const [modelParams] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
      days: 28,
      startDate: tomorrow.toISOString().split('T')[0],
      region: ''
    };
  });

  // Enhanced export hook
  const handleExport = useExportFunctions(
    dataSummary,
    dailyForecastData,
    simpleForecastData,
    promotionalData,
    aggregationState,
    adHocData,
    customerOrdersState,
    user?.role || 'demand_planner'
  );


  // Load initial data
  const loadDataSummary = useCallback(async () => {
    try {
      const data = await apiGet('/api/data-summary');
      setDataSummary(data);
      setError(null);
    } catch (err) {
      console.error('Error loading data summary:', err);
      setError(`Failed to load data summary: ${err.message}`);
    }
  }, []);

  const loadSimpleForecast = useCallback(async () => {
    try {
      const forecastData = await apiGet(`/api/forecast?days=${modelParams.days}&start_date=${modelParams.startDate}&region=${encodeURIComponent(modelParams.region)}`);
      setSimpleForecastData(forecastData);
    } catch (err) {
      console.error('Error loading simple forecast:', err);
      setError(`Failed to load forecast: ${err.message}`);
    }
  }, [modelParams.days, modelParams.startDate, modelParams.region]);

  // Initialize data on component mount / connection ready
  useEffect(() => {
    if (connectionStatus === 'connected') {
      loadDataSummary();
      loadSimpleForecast();
    }
  }, [connectionStatus, loadDataSummary, loadSimpleForecast]);

  const handleDailyForecast = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...params,
        forecast_granularity: params.forecast_granularity || deriveGranularityFromLocation(params.location),
      };
      const data = await apiPost('/api/daily-forecast', payload);
      setDailyForecastData(data);
    } catch (err) {
      console.error('Error generating daily forecast:', err);
      setError(`Daily forecast failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePromotionalForecast = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...params,
        forecast_granularity: params.forecast_granularity || deriveGranularityFromLocation(params.location),
      };
      const data = await apiPost('/api/promotional-forecast', payload);
      data._request_params = payload;
      setPromotionalData(data);
    } catch (err) {
      console.error('Error generating promotional forecast:', err);
      setError(`Promotional forecast failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAdHocAdjustment = useCallback(async (params) => {
    setAdHocLoading(true);
    setError(null);
    try {
      const requesterRole = user?.role || 'demand_planner';
      const requesterUser = user?.username || user?.name || user?.email || '';
      const currentForecast = Number(params.current_forecast);
      const adjustmentValue = Number(params.adjustment_value);
      const computed = calculateAdjustment(
        params.current_forecast,
        params.adjustment_value,
        params.adjustment_type
      );
      const payload = {
        ...params,
        requested_by_role: requesterRole,
        requested_by_user: requesterUser,
        current_forecast: Number.isFinite(currentForecast) ? currentForecast : params.current_forecast,
        adjustment_value: Number.isFinite(adjustmentValue) ? adjustmentValue : params.adjustment_value,
        new_forecast_value: params.new_forecast_value ?? computed?.newValue ?? currentForecast,
        timestamp: params.timestamp ?? new Date().toISOString(),
      };
      const data = await apiPost('/api/adhoc-adjustment', payload);
      setAdHocData({ ...payload, ...data });
      if (data?.status === 'applied') {
        loadDataSummary();
      }
    } catch (err) {
      console.error('Ad-hoc adjustment failed:', err);
      setError(`Ad-hoc adjustment failed: ${err.message}`);
    } finally {
      setAdHocLoading(false);
    }
  }, [user, loadDataSummary]);

  useEffect(() => {
    const requestId = adHocData?.request_id;
    const status = String(adHocData?.approval_status || adHocData?.status || '').toLowerCase();
    if (!requestId || status !== 'pending') return undefined;

    let stopped = false;
    const refreshRequestStatus = async () => {
      try {
        let requestRecord = null;
        let data = null;
        try {
          data = await apiGet(`/api/adhoc-requests/${requestId}`);
          requestRecord = data?.request;
        } catch (directErr) {
          const allData = await apiGet('/api/adhoc-requests?status=');
          requestRecord = (Array.isArray(allData?.requests) ? allData.requests : []).find(item =>
            String(item?.request_id || '') === String(requestId) ||
            String(item?.adjustment_id || '') === String(requestId) ||
            String(item?.approved_adjustment_id || '') === String(requestId)
          );
          data = { status: requestRecord?.approval_status || requestRecord?.status, request: requestRecord };
        }
        const nextStatus = String(requestRecord?.approval_status || data?.status || '').toLowerCase();
        if (!stopped && requestRecord && nextStatus && nextStatus !== 'pending') {
          setAdHocData(prev => ({
            ...prev,
            ...requestRecord,
            status: nextStatus,
            approval_status: nextStatus,
            adjustment_id: requestRecord.approved_adjustment_id || requestRecord.adjustment_id || prev?.adjustment_id || requestId,
            request_id: requestId,
          }));
        }
      } catch (err) {
        console.error('Failed to refresh ad-hoc approval status:', err);
      }
    };

    refreshRequestStatus();
    const timer = window.setInterval(refreshRequestStatus, 5000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [adHocData?.request_id, adHocData?.approval_status, adHocData?.status]);

  const mergeReviewedAdhocRequest = useCallback((requestId, requestRecord, fallbackStatus = '') => {
    if (!requestId || !requestRecord) return;
    setAdHocData(prev => {
      if (!prev || String(prev.request_id || '') !== String(requestId)) return prev;
      const nextStatus = String(requestRecord.approval_status || fallbackStatus || requestRecord.status || '').toLowerCase();
      return {
        ...prev,
        ...requestRecord,
        status: nextStatus || prev.status,
        approval_status: nextStatus || requestRecord.approval_status || prev.approval_status,
        adjustment_id: requestRecord.approved_adjustment_id || requestRecord.adjustment_id || prev.adjustment_id || requestId,
        request_id: requestId,
      };
    });
  }, []);

  const handleApproveAdhocRequest = useCallback(async (requestId) => {
    if (!requestId) return;
    const reviewer = user?.username || user?.name || user?.role || 'admin';
    setProcessingAdhocRequestId(requestId);
    setError(null);
    try {
      const data = await apiPost(`/api/adhoc-requests/${requestId}/approve`, {
        reviewed_by: reviewer,
        review_notes: '',
      });
      mergeReviewedAdhocRequest(requestId, data?.request, data?.status || 'approved');
      await loadAdhocRequests();
      loadDataSummary();
    } catch (err) {
      console.error('Approve ad-hoc request failed:', err);
      setError(`Failed to approve ad-hoc request: ${err.message}`);
    } finally {
      setProcessingAdhocRequestId('');
    }
  }, [loadAdhocRequests, mergeReviewedAdhocRequest, loadDataSummary, user]);

  const handleRejectAdhocRequest = useCallback(async (requestId) => {
    if (!requestId) return;
    const reviewer = user?.username || user?.name || user?.role || 'admin';
    setProcessingAdhocRequestId(requestId);
    setError(null);
    try {
      const reviewNotes = window.prompt('Optional rejection note:', '') || '';
      const data = await apiPost(`/api/adhoc-requests/${requestId}/reject`, {
        reviewed_by: reviewer,
        review_notes: reviewNotes,
      });
      mergeReviewedAdhocRequest(requestId, data?.request, data?.status || 'rejected');
      await loadAdhocRequests();
    } catch (err) {
      console.error('Reject ad-hoc request failed:', err);
      setError(`Failed to reject ad-hoc request: ${err.message}`);
    } finally {
      setProcessingAdhocRequestId('');
    }
  }, [loadAdhocRequests, mergeReviewedAdhocRequest, user]);

  const handleScenarioAnalysis = useCallback(async ({ scenario, forecast_days = 28, sku_id, channel, location, brand, start_date, forecast_granularity }) => {
    const requestSeq = scenarioRequestSeqRef.current + 1;
    scenarioRequestSeqRef.current = requestSeq;
    setScenarioLoading(true);
    setSimulationChartContext(null);
    setError(null);
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const resolvedStartDate = start_date || tomorrow.toISOString().split('T')[0];
      const resolvedGranularity = forecast_granularity || deriveGranularityFromLocation(location);
      const result = await apiPost('/api/scenario-forecast', {
        forecast_days,
        start_date: resolvedStartDate,
        scenario,
        brand: brand || null,
        sku_id: sku_id || null,
        channel: channel || null,
        location: location || null,
        forecast_granularity: resolvedGranularity || null,
      });
      if (scenarioRequestSeqRef.current === requestSeq) {
        setSimpleForecastData(result);
      }
    } catch (err) {
      if (scenarioRequestSeqRef.current !== requestSeq) {
        return;
      }
      console.error('Scenario forecast failed:', err);
      setError('Scenario forecast failed: ' + err.message);
    } finally {
      if (scenarioRequestSeqRef.current === requestSeq) {
        setScenarioLoading(false);
      }
    }
  }, []);

  const isBusy = loading || adHocLoading || scenarioLoading;
  const busyMessage = adHocLoading
      ? 'Applying ad-hoc adjustment...'
      : scenarioLoading
        ? 'Running scenario simulation...'
        : loading
          ? (activeWindow === 'promotional'
            ? 'Generating promotional forecast...'
            : activeWindow === 'dashboard'
              ? 'Generating daily forecast...'
              : 'Loading...')
          : '';

  const renderWindowContent = (windowId) => {
    switch (windowId) {
      case 'dashboard':
        return (
          <DashboardWindow
            onDailyForecast={handleDailyForecast}
            loading={loading}
            dataSummary={dataSummary}
            dailyForecastData={dailyForecastData}
            onKpiContextChange={setDashboardKpiContext}
            onChartContextChange={setDashboardChartContext}
          />
        );
      case 'customer_orders':
        return <CustomerOrdersWindow onStateChange={setCustomerOrdersState} />;
      case 'aggregation':
        return (
          <AggregationWindow onStateChange={setAggregationState} />
        );
      case 'adhoc':
        return (
          <AdHocWindow
            onAdHocAdjustment={handleAdHocAdjustment}
            loading={adHocLoading}
            dataSummary={dataSummary}
            adHocData={adHocData}
          />
        );
      case 'simulation':
        return (
          <SimulationWindow
            onRunScenario={handleScenarioAnalysis}
            loading={scenarioLoading}
            simpleForecastData={simpleForecastData}
            onChartContextChange={setSimulationChartContext}
            onScenarioContextChange={setSimulationScenarioContext}
          />
        );
      case 'promotional':
        return (
          <PromotionalWindow
            onPromotionalForecast={handlePromotionalForecast}
            loading={loading}
            dataSummary={dataSummary}
            promotionalData={promotionalData}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    const windowOrder = ['dashboard', 'simulation', 'promotional', 'adhoc', 'aggregation', 'customer_orders'];
    const mountedWindows = windowOrder.filter(windowId => visitedWindows.includes(windowId) || windowId === activeWindow);

    return (
      <div className="app-window-keepalive">
        {mountedWindows.map(windowId => (
          <div
            key={windowId}
            className={`app-window-pane ${windowId === activeWindow ? 'active' : ''}`}
            aria-hidden={windowId !== activeWindow}
          >
            {renderWindowContent(windowId)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`app ${copilotOpen ? 'copilot-open' : ''}`}
      style={{ '--copilot-width': `${copilotWidth}px` }}
    >
      <style>{dropdownStyles}</style>
      <GlobalLoadingOverlay visible={isBusy} message={busyMessage} />
      <TopHeader
        onExport={handleExport}
        onTitleClick={() => setActiveWindow('dashboard')}
        isAdmin={isAdmin}
        adhocRequests={adhocRequests}
        adhocRequestsLoading={adhocRequestsLoading}
        onRefreshAdhocRequests={loadAdhocRequests}
        onApproveAdhocRequest={handleApproveAdhocRequest}
        onRejectAdhocRequest={handleRejectAdhocRequest}
        processingAdhocRequestId={processingAdhocRequestId}
        pendingUsers={pendingUsers}
        pendingUsersLoading={pendingUsersLoading}
        onRefreshPendingUsers={loadPendingUsers}
        onApproveUser={handleApproveUser}
        onRejectUser={handleRejectUser}
        processingPendingUserId={processingPendingUserId}
      />
      <Layout
        activeWindow={activeWindow}
        setActiveWindow={setActiveWindow}
        connectionStatus={connectionStatus}
        setConnectionStatus={setConnectionStatus}
        setError={setError}
      >
        <ErrorDisplay
          error={error}
          onClose={() => setError(null)}
        />
        {renderContent()}
      </Layout>
      <ForecAICopilot
        activeWindow={activeWindow}
        onOpenChange={setCopilotOpen}
        panelWidth={copilotWidth}
        onPanelWidthChange={(nextWidth) => setCopilotWidth(clampCopilotWidth(nextWidth))}
        dataSummary={dataSummary}
        dashboardKpiContext={dashboardKpiContext}
        dashboardChartContext={dashboardChartContext}
        dailyForecastData={dailyForecastData}
        simpleForecastData={simpleForecastData}
        simulationChartContext={simulationChartContext}
        simulationScenarioContext={simulationScenarioContext}
        promotionalData={promotionalData}
        aggregationState={aggregationState}
        customerOrdersState={customerOrdersState}
        adHocData={adHocData}
      />
    </div>
  );
};

// Main App Router
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginWindow />} />
          <Route path="/register" element={<SignupWindow />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard/*" element={
            <ProtectedRoute>
              <DashboardApp />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
